import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { DownloadService } from '@vk-hr-tek/core/download';

import {
  CompanyTemplatesRequest,
  DownloadTemplateFileParams,
  DownloadTemplateFileQuery,
  TemplateHistoryResponse,
  TemplatesListResponse,
  TemplatesService,
} from '@app/gen/templates';
import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import { Entity } from '@app/types';

import { ThunkExtra } from '../../../../app/store';
import { SettingsState, SettingsWithRootState } from '../../settings.state';

export const getTemplatesList = createAsyncThunk<
  TemplatesListResponse,
  CompanyTemplatesRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getTemplatesList',
  async ({ company_id }, { rejectWithValue, extra: { inject } }) => {
    try {
      const templatesService = inject(TemplatesService);

      const result = await templatesService.getTemplateList({
        body: { company_id },
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getTemplateHistory = createAsyncThunk<
  TemplateHistoryResponse,
  { template_id: string },
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getTemplateHistory',
  async ({ template_id }, { rejectWithValue, extra: { inject } }) => {
    try {
      const templatesService = inject(TemplatesService);

      return await templatesService.getTemplateHistory({
        params: { template_id },
      });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const downloadTemplate = createAsyncThunk<
  void,
  DownloadTemplateFileQuery & DownloadTemplateFileParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/downloadTemplate',
  async ({ template_id, file_id }, { rejectWithValue, extra: { inject } }) => {
    try {
      const templatesService = inject(TemplatesService);

      await templatesService
        .downloadTemplateFile({
          params: { template_id },
          ...(file_id ? { query: { file_id } } : {}),
        })
        .then(async ({ file, filename }) => {
          await inject(DownloadService).doDownload(
            file,
            filename || 'document',
          );
        });
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsTemplatesReducer = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getTemplatesList.pending, (state) => {
    state.templates = {
      status: 'loading',
      items: [],
      error: null,
    };
  });
  builder.addCase(getTemplatesList.fulfilled, (state, { payload }) => {
    state.templates = {
      status: 'complete',
      items: payload.templates,
      error: null,
    };
  });
  builder.addCase(getTemplatesList.rejected, (state, { payload, error }) => {
    state.templates = {
      status: 'failed',
      items: [],
      error:
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError),
    };
  });

  builder.addCase(getTemplateHistory.pending, (state) => {
    state.templateHistory = {
      status: 'loading',
      history: [],
      error: null,
    };
  });
  builder.addCase(getTemplateHistory.fulfilled, (state, { payload }) => {
    state.templateHistory = {
      status: 'complete',
      history: payload.history,
      error: null,
    };
  });
  builder.addCase(getTemplateHistory.rejected, (state, { payload, error }) => {
    state.templateHistory = {
      status: 'failed',
      history: [],
      error:
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError),
    };
  });
};
